import { KPI_UNAVAILABLE_NOTE, KPI_UNAVAILABLE_VALUE } from "../workspaceFrameCopy";

/**
 * The Production KPI slot set (WORKSPACE-FEATURE-001 S9).
 *
 * AW-VIS-2 TURNED THIS FROM A REGION INTO ITS CONTENT
 * ---------------------------------------------------
 * PR-A made the KPIs their own stacked band beneath the campaign
 * section. The authoritative design has no such band: KPIs sit at the
 * left of the single operational row, with campaign identity and the
 * campaign controls at its right edge (reference
 * `docs/design/prototypes/workspace-v1-reference.png`; original
 * `WorkspaceOperationalRow` at commit 23d500e27).
 *
 * So the region component is gone and its content stayed. The slots,
 * their order, their labels and -- critically -- their honest
 * unavailable value are unchanged, and `WorkspaceOperationalRow` now
 * renders them under the same `workspace-kpi-region` and
 * `workspace-kpi-<slot>` identities.
 *
 * WHY THIS RENDERS NO NUMBERS
 * ---------------------------
 * The specification's MVP KPI set is Talk Time, Active Time, Submitted
 * Apps Today, Scheduled Apps Today and Dialable Leads. The workspace
 * creates the *slots* so it stops reflowing when those contracts land.
 * It creates none of the values, because none of them has a governed
 * backend contract today, and S9.6 is explicit that a KPI which cannot
 * be loaded must say so rather than render a figure.
 *
 * Fabricating any of these would be worse than omitting them: an agent
 * who reads "Talk Time 00:00:00" believes MissionAssist measured their
 * talk time and found none. S7.8 requires unknown and zero to stay
 * distinguishable, which is why every slot carries the word
 * `Unavailable` and never a digit.
 *
 * WHY DIALABLE LEADS IS ABSENT FROM THIS SET ENTIRELY
 * ---------------------------------------------------
 * It is the one KPI for which a figure *does* exist -- and that figure
 * is the known-incorrect sum recorded as P2-R2. The Production shell
 * already renders it beside the campaign name, and already declines to
 * render it at all after a restoration (`available_leads: null`, with
 * its own comment explaining that an em dash is the honest answer).
 *
 * Three options were available and two were rejected:
 *
 *   - Promote the existing figure into this set as the spec's
 *     persistent Dialable Leads KPI. REJECTED: that would present a
 *     known-wrong number in the one place the specification says must be
 *     continuously trustworthy (S9.3), and would show it twice.
 *   - Render a Dialable Leads slot here as `Unavailable` while the
 *     campaign region continues to show the number. REJECTED: the two
 *     would contradict each other on screen in the same frame.
 *   - Leave Dialable Leads exactly where and as it is today, and define
 *     slots for the four KPIs that have no surface at all. CHOSEN.
 *     This invents no value and leaves the real repair (a trustworthy
 *     count) to whoever owns P2-R2.
 */

export const KPI_SLOTS = Object.freeze([
  { key: "talk-time", label: "Talk Time" },
  { key: "active-time", label: "Active Time" },
  { key: "submitted-apps", label: "Submitted Apps Today" },
  { key: "scheduled-apps", label: "Scheduled Apps Today" },
]);

/**
 * The slots in the shape `WorkspaceOperationalRow` consumes. Frozen at
 * module scope rather than built per render: there is nothing dynamic
 * about it, and a stable identity makes it obvious that no call, poll
 * or state change can ever put a figure here.
 */
export const PRODUCTION_KPI_ITEMS = Object.freeze(
  KPI_SLOTS.map((slot) =>
    Object.freeze({ key: slot.key, label: slot.label, value: KPI_UNAVAILABLE_VALUE })
  )
);

export { KPI_UNAVAILABLE_NOTE };
